import imageUrlBuilder, { type SanityImageSource } from '@sanity/image-url';

const projectId = process.env.EXPO_PUBLIC_SANITY_PROJECT_ID;
const dataset = process.env.EXPO_PUBLIC_SANITY_DATASET;
const apiVersion = '2025-01-01';

if (!projectId || !dataset) {
  throw new Error(
    'Missing EXPO_PUBLIC_SANITY_PROJECT_ID or EXPO_PUBLIC_SANITY_DATASET in .env.local',
  );
}

// The app only ever READS content — GROQ `.fetch()` in the hooks/prefetch and
// `urlForImage()` below. It never uses listeners, mutations or auth, so we call
// Sanity's query API directly instead of pulling in the full `@sanity/client`,
// which drags rxjs + get-it into the web bundle (~207KB of JS to parse/execute
// on load). The request shape (apicdn host, `v<apiVersion>/data/query`, params
// as `$name=<json>`) mirrors exactly what the client sent, so CORS/CDN behaviour
// is unchanged. `fetch`/`URL` are global in both the browser and the Node SSG
// pass that pre-renders article pages.
const queryUrl = `https://${projectId}.apicdn.sanity.io/v${apiVersion}/data/query/${dataset}`;

async function fetchQuery<T>(query: string, params: Record<string, unknown> = {}): Promise<T> {
  const url = new URL(queryUrl);
  url.searchParams.set('query', query);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(`$${key}`, JSON.stringify(value));
  }
  url.searchParams.set('returnQuery', 'false');

  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new Error(`Sanity query failed: ${res.status} ${res.statusText}`);
  }
  const json = (await res.json()) as { result: T };
  return json.result;
}

// Preserve the `.fetch(query, params)` shape the hooks/prefetch already call.
export const sanityClient = { fetch: fetchQuery };

const builder = imageUrlBuilder({ projectId, dataset });

// `auto('format')` lets Sanity's CDN negotiate WebP/AVIF off the request's
// Accept header instead of returning the source file verbatim. Without it a
// PNG-sourced article hero shipped at its original weight — the 1600px variant
// of one hero measured 3,289,364 bytes as PNG vs 87,386 as WebP (-97%). Set
// here rather than at the call sites so every consumer (hero, OG image, cards,
// Portable Text body images) inherits it.
export function urlForImage(source: SanityImageSource) {
  return builder.image(source).auto('format').quality(75);
}
